import '../styles.scss';

import { Box, Grid, Typography } from '@mui/material';
import { useContext } from 'react';
import { ChartTypeEnum } from '../../../../../model/chart_type.enum';
import { CurrencyTypeEnum } from '../../../../../model/currency_type.enum';
import { OverviewContext } from '../../../../../providers/overview-provider';
import { lightBlue } from '../../../../../theme/colors';
import { PerpetualDataPoint } from '../../../../../providers/market-data/market-data.types';

interface MarketHistoryHeaderProps {
  chartType: ChartTypeEnum;
  isLoading: boolean;
  hoverData: PerpetualDataPoint | null;
  currency: CurrencyTypeEnum;
}

export const MarketHistoryHeader = ({
  chartType,
  isLoading,
  hoverData,
  currency,
}: MarketHistoryHeaderProps) => {
  const { label } = useContext(OverviewContext);
  const range = '';

  const getChangeClass = (close: number, open: number) => {
    if (isLoading) return 'loading-dots';
    return close >= open ? 'positive' : 'negative';
  };

  if (!hoverData) {
    return <div className="chart-header empty" />;
  }

  if (
    chartType === ChartTypeEnum.CandleStick ||
    chartType === ChartTypeEnum.HollowCandles ||
    chartType === ChartTypeEnum.Bars
  ) {
    const { open = 0, high = 0, low = 0, value: close = 0 } = hoverData;
    const change = Number((close - open).toFixed(2));
    const percent = open ? Number(((change / open) * 100).toFixed(2)) : 0;

    return (
      <Box className="chart-header">
        <Grid container direction="row" spacing={2} className="pointData">
          <Grid item>
            {currency} {label}
          </Grid>
          <Grid item>{range}</Grid>

          {(
            [
              ['O', open],
              ['H', high],
              ['L', low],
              ['C', close],
            ] as [string, number][]
          ).map(([letter, val]) => (
            <Grid item key={letter}>
              {letter}
              <Typography className={`header-data ${getChangeClass(close, open)}`} display="inline">
                {isLoading ? '…' : val}
              </Typography>
            </Grid>
          ))}

          <Grid item>
            <Typography className={`header-data ${getChangeClass(close, open)}`} display="inline">
              {isLoading ? '…' : Math.abs(change)}
            </Typography>
          </Grid>
          <Grid item>
            <Typography className={`header-data ${getChangeClass(close, open)}`} display="inline">
              ({isLoading ? '…' : percent}%)
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (
    chartType === ChartTypeEnum.Line ||
    chartType === ChartTypeEnum.LineWMarkers ||
    chartType === ChartTypeEnum.Area ||
    chartType === ChartTypeEnum.Columns
  ) {
    const { value = 0 } = hoverData;
    return (
      <Box className="chart-header">
        <Grid container direction="row" spacing={2} className="pointData">
          <Grid item>
            {currency} {label}
          </Grid>
          <Grid item>{range}</Grid>
          <Grid item>
            <Typography className="header-data" color={lightBlue} display="inline">
              {isLoading ? '…' : value}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return <div className="chart-header empty" />;
};
